import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

interface CredentialDialogProps {
  open: boolean;
  credentialLabel: string;
  accountName: string;
  currentValue?: string | null;
  helperText?: string;
  onClose: () => void;
  onSave: (value: string) => Promise<void>;
}

export default function CredentialDialog({
  open,
  credentialLabel,
  accountName,
  currentValue,
  helperText,
  onClose,
  onSave,
}: CredentialDialogProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setValue('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || submitting) return;
    onClose();
  };

  const handleSave = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setError(null);
    setSubmitting(true);
    try {
      await onSave(trimmed);
      onClose();
    } catch (err: any) {
      setError(err.message || `Failed to save the ${credentialLabel}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Replace {credentialLabel}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2} sx={{ pt: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Current value for {accountName}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
              color={currentValue ? 'text.primary' : 'text.secondary'}
            >
              {currentValue || 'Not set'}
            </Typography>
          </Box>

          <TextField
            label={`New ${credentialLabel}`}
            size="small"
            type="password"
            fullWidth
            autoComplete="new-password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={submitting}
            helperText={helperText}
            autoFocus
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={submitting || !value.trim()}
          sx={{ backgroundColor: '#465fff', '&:hover': { backgroundColor: '#3641f5' } }}
        >
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
